<!--
	SectionHead — the one heading block every section opens with, so the page has a
	spine instead of nine bespoke headers.

	The eyebrow is a real index ("02 / THE LEVERAGE") in the readout voice, led by a
	small filled square in the accent. That square is the only decoration allowed in
	a heading: it's the plotter's origin mark, and it gives the eye a fixed left edge
	to run down as it scrolls.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';

	let {
		index,
		label,
		heading,
		/** Lead paragraph under the heading. Prefer this over a <p> in the caller. */
		intro,
		/** Anything extra under the intro — a CTA, a readout row. */
		children,
		level = 2,
		/** Centre the block. Use for full-bleed set pieces only. */
		align = 'start',
		class: klass = ''
	}: {
		index?: string;
		label?: string;
		heading: string;
		intro?: string;
		children?: Snippet;
		level?: 1 | 2;
		align?: 'start' | 'center';
		class?: string;
	} = $props();
</script>

<div class="head" data-align={align} class:head--dark={false}>
	{#if label}
		<p class="head__eyebrow">
			<span class="head__tick" aria-hidden="true"></span>
			{#if index}<span class="head__index">{index}</span>{/if}
			<span>{label}</span>
		</p>
	{/if}

	<svelte:element this={`h${level}`} class="head__h {klass}" data-level={level}>
		{heading}
	</svelte:element>

	{#if intro}
		<p class="head__intro">{intro}</p>
	{/if}

	{#if children}
		<div class="head__extra">{@render children()}</div>
	{/if}
</div>

<style>
	.head[data-align='center'] {
		text-align: center;
		margin-inline: auto;
	}
	.head[data-align='center'] .head__eyebrow {
		justify-content: center;
	}
	.head[data-align='center'] .head__intro {
		margin-inline: auto;
	}

	.head__eyebrow {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-400);
		margin-bottom: 1.25rem;
	}
	.head__tick {
		width: 6px;
		height: 6px;
		flex: none;
		background-color: var(--color-cut-600);
	}
	.head__index {
		/* ink-400, not ink-300: the index is text (WCAG AA 4.5:1), not decoration. */
		color: var(--color-ink-400);
	}

	.head__h[data-level='1'] {
		font-size: var(--text-display);
		line-height: 0.96;
		letter-spacing: -0.035em;
	}
	.head__h[data-level='2'] {
		font-size: var(--text-h2);
		line-height: 1.04;
		letter-spacing: -0.025em;
	}
	.head__h {
		font-weight: 600;
		max-width: 22ch;
	}
	.head[data-align='center'] .head__h {
		max-width: 24ch;
		margin-inline: auto;
	}

	.head__intro {
		margin-top: 1.25rem;
		max-width: var(--container-measure);
		font-size: var(--text-lead);
		line-height: 1.5;
		letter-spacing: -0.01em;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}

	.head__extra {
		margin-top: 2rem;
	}

	/* On a dark section the head inverts wholesale. */
	:global(.sec--dark) .head__h {
		color: var(--color-bone-100);
	}
	:global(.sec--dark) .head__intro {
		color: var(--color-bone-300);
	}
	:global(.sec--dark) .head__eyebrow {
		color: var(--color-bone-500);
	}
	:global(.sec--dark) .head__index {
		color: var(--color-bone-500);
	}
	:global(.sec--dark) .head__tick {
		background-color: var(--color-cut-400);
	}
</style>
